import {
  Box,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckoutStepsAccordion from "../../components/CheckoutStepsAccordion";
import IdentificationStep from "../../components/Checkout/IdentificationStep";
import DeliveryStep from "../../components/Checkout/DeliveryStep";
import SummaryStep from "../../components/Checkout/SummaryStep";
import AddressDialog from "../../components/Checkout/AddressDialog";
import useCheckout from "../../hooks/useCheckout";
import PaymentStep from "../../components/Checkout/PaymentStep";

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.success.main,
      borderTopWidth: 3,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.success.main,
      borderTopWidth: 3,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.grey[300],
    borderTopWidth: 3,
  },
}));

const Checkout = () => {
  const {
    isLoadingUrbanPartner,
    isLoadingSeed,
    isLoadingFair,
    isLoadingCreateOrder,
    urbanPartner,
    address,
    deliveryMethod,
    deliveryMethodId,
    setDeliveryMethodId,
    open,
    setOpen,
    isFormValid,
    navigate,
    seed,
    activeStep,
    setActiveStep,
    subtotal,
    credit,
    deliveryWeekday,
    total,
    handleCreateOrder,
    handleSave,
    handleNext,
    handleBack,
    slug,
    addressError,
    paymentOrder,
  } = useCheckout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const steps = ["Identificação", "Entrega", "Resumo", "Pagamento"];

  if (
    isLoadingSeed ||
    isLoadingFair ||
    (isLoadingUrbanPartner && !urbanPartner)
  ) {
    return <CircularProgress />;
  }

  if (!seed) {
    return <></>;
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <IdentificationStep
            urbanPartner={urbanPartner}
            slug={slug}
            navigate={navigate}
          />
        );
      case 1:
        return (
          <DeliveryStep
            isFormValid={isFormValid}
            deliveryMethods={seed.delivery_methods}
            deliveryMethodId={deliveryMethodId}
            setDeliveryMethodId={setDeliveryMethodId}
            address={address}
            seedAddress=""
            seedAddressObj={seed.address}
            deliveryWeekday={deliveryWeekday}
            onChangeAddress={() => {
              setOpen(true);
            }}
            addressError={addressError}
          />
        );
      case 2:
        return (
          <SummaryStep
            subtotal={subtotal}
            deliveryFee={deliveryMethod ? deliveryMethod.fee : 0}
            credit={credit}
            total={total()}
          />
        );
      case 3:
        return (
          <PaymentStep
            seed={seed}
            orderId={paymentOrder?.id}
            total={paymentOrder?.total ?? null}
            isHomeDelivery={
              deliveryMethod ? deliveryMethod.is_home_delivery : true
            }
            slug={slug}
            isLoadingSeed={isLoadingSeed}
            isLoadingUP={isLoadingUrbanPartner}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <div style={{ margin: "0 auto", maxWidth: "900px", padding: "2rem" }}>
        {isMobile ? (
          <CheckoutStepsAccordion
            steps={steps}
            activeStep={activeStep}
            renderStepContent={renderStepContent}
            onBack={(step) => {
              if (step === 0) {
                navigate(`/${slug}`);
              } else {
                handleBack();
              }
            }}
            onNext={(step) => {
              if (step === 0 && !urbanPartner) return;
              if (step === 1 && !deliveryMethod) return;
              if (step === 2) {
                handleCreateOrder();
                return;
              }
              setActiveStep((prev) => prev + 1);
            }}
            canGoNext={(step) => {
              return !(
                (step === 0 && !urbanPartner) ||
                (step === 1 &&
                  (!deliveryMethod ||
                    (deliveryMethod.is_home_delivery && !address))) ||
                (step === 2 && isLoadingCreateOrder)
              );
            }}
            canGoBack={() => true}
            nextLabel="Próximo"
            backLabel="Voltar"
          />
        ) : (
          <>
            <Stepper
              activeStep={activeStep}
              connector={<CustomConnector />}
              sx={{
                mb: 4,
                "& .MuiStepLabel-root .Mui-active": {
                  color: "primary.main",
                },
                "& .MuiStepLabel-root .Mui-disabled": {
                  color: "text.disabled",
                },
                "& .MuiStepLabel-labelContainer": {
                  fontSize: "1.4rem",
                },
                "& .MuiStepIcon-root": {
                  fontSize: "2rem",
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: "success.main",
                },
                "& .MuiStepIcon-root.Mui-active": {
                  color: "warning.main",
                },
                "& .MuiStepIcon-root.Mui-disabled": {
                  color: "text.disabled",
                },
                "& .MuiStep-root.Mui-active": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  borderRadius: "8px",
                  padding: "8px",
                  margin: "-8px",
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {renderStepContent(activeStep)}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              {activeStep !== steps.length - 1 && (
                <Button
                  onClick={() => {
                    if (activeStep === 0) {
                      navigate(`/${slug}`);
                    } else {
                      handleBack();
                    }
                  }}
                  sx={{ mr: 1 }}
                >
                  Voltar
                </Button>
              )}
              {activeStep !== steps.length - 1 && (
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={
                      (activeStep === 0 && !urbanPartner) ||
                      (activeStep === 1 &&
                        (!deliveryMethod ||
                          (deliveryMethod.is_home_delivery && !address))) ||
                      (activeStep === 2 && isLoadingCreateOrder)
                    }
                  >
                    Próximo
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
        <AddressDialog
          open={open}
          onClose={() => setOpen(false)}
          onSave={handleSave}
          defaultAddress={urbanPartner?.address || ""}
          defaultAddressObj={urbanPartner?.address_obj}
        />
      </div>
    </Box>
  );
};

export default Checkout;
